import React, { createContext, useContext } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';

interface AnswerContextValue {
  messageIndex: number;
  isDinacharya: boolean;
  onSend: (text: string) => void;
}

const AnswerContext = createContext<AnswerContextValue | null>(null);

function useAnswerContext() {
  const context = useContext(AnswerContext);
  if (!context) throw new Error('Answer renderer requires AnswerContext');
  return context;
}

// Stable component types preserve selected DOM nodes while context stays current.
const components: Components = {
  h1: function AnswerHeading({ node: _node, className: _className, ...props }) {
    const { isDinacharya } = useAnswerContext();
    return (
      <h1
        className={isDinacharya
          ? '!mt-6 !mb-4 break-words !text-3xl !font-bold !leading-tight !tracking-normal text-gray-900 dark:text-gray-100'
          : _className}
        {...props}
      />
    );
  },
  sup: function AnswerSuperscript({ node: _node, ...props }) {
    const { messageIndex: index } = useAnswerContext();
    const children = props.children as React.ReactElement[];
    if (children && children[0] && children[0].props) {
      const href = children[0].props.href;
      if (typeof href === 'string' && href.startsWith('#user-content-fn-')) {
        const newHref = href.replace('#user-content-fn-', `#user-content-fn-${index}-`);
        return <sup><a {...children[0].props} href={newHref}>{children[0].props.children}</a></sup>;
      }
    }
    return <sup {...props} />;
  },
  li: function AnswerListItem({ node: _node, ...props }) {
    const { messageIndex: index } = useAnswerContext();
    if (props.id && typeof props.id === 'string') {
      if (props.id.startsWith('user-content-fn-')) {
        const newId = props.id.replace('user-content-fn-', `user-content-fn-${index}-`);
        return <li {...props} id={newId}>{props.children}</li>;
      } else if (props.id.startsWith('user-content-fnref-')) {
        const newId = props.id.replace('user-content-fnref-', `user-content-fnref-${index}-`);
        return <li {...props} id={newId}>{props.children}</li>;
      }
    }
    return <li {...props}>{props.children}</li>;
  },
  p: function AnswerParagraph({ node: _node, ...props }) {
    const { messageIndex: index } = useAnswerContext();
    if (props.id && typeof props.id === 'string') {
      if (props.id.startsWith('user-content-fnref-')) {
        const newId = props.id.replace('user-content-fnref-', `user-content-fnref-${index}-`);
        return <p {...props} id={newId}>{props.children}</p>;
      } else if (props.id.startsWith('user-content-fn-')) {
        const newId = props.id.replace('user-content-fn-', `user-content-fn-${index}-`);
        return <p {...props} id={newId}>{props.children}</p>;
      }
    }
    return <p {...props}>{props.children}</p>;
  },
  div: function AnswerDiv({ node: _node, ...props }) {
    const { messageIndex: index } = useAnswerContext();
    if (props.id && typeof props.id === 'string') {
      if (props.id.startsWith('user-content-fnref-')) {
        const newId = props.id.replace('user-content-fnref-', `user-content-fnref-${index}-`);
        return <div {...props} id={newId}>{props.children}</div>;
      } else if (props.id.startsWith('user-content-fn-')) {
        const newId = props.id.replace('user-content-fn-', `user-content-fn-${index}-`);
        return <div {...props} id={newId}>{props.children}</div>;
      }
    }
    return <div {...props}>{props.children}</div>;
  },
  a: function AnswerAnchor({ node: _node, ...props }) {
    const { onSend } = useAnswerContext();
    const { href = '' } = props;
    if (href === '#') {
      const text = props.children?.toString() || '';
      return (
        <span
          onClick={e => {
            e.preventDefault();
            onSend('Elaborate comprehensively about: ' + text);
          }}
          style={{ cursor: 'pointer', textDecoration: 'underline', color: '#1d4ed8' }}
        >
          {props.children}
        </span>
      );
    }
    return <a {...props}>{props.children}</a>;
  },
  u: function AnswerUnderline({ node: _node, ...props }) {
    const { onSend } = useAnswerContext();
    const { href = '' } = props as { href?: string };
    if (href === '#') {
      const text = props.children?.toString() || '';
      return (
        <span
          onClick={e => {
            e.preventDefault();
            onSend('Elaborate comprehensively about: ' + text);
          }}
          style={{ cursor: 'pointer', textDecoration: 'underline', color: '#1d4ed8' }}
        >
          {props.children}
        </span>
      );
    }
    return <u {...props} />;
  },
  pre: ({ node: _node, ...props }) => <pre className="whitespace-pre-wrap" {...props} />,
};

interface AnswerMarkdownProps extends AnswerContextValue {
  text: string;
}

export default function AnswerMarkdown({ text, messageIndex, isDinacharya, onSend }: AnswerMarkdownProps) {
  return (
    <AnswerContext.Provider value={{ messageIndex, isDinacharya, onSend }}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} components={components}>
        {text}
      </ReactMarkdown>
    </AnswerContext.Provider>
  );
}
